import React, { useState, useEffect } from 'react';
import { FolderPlus, Edit2, Trash2, Eye, EyeOff, GripVertical } from 'lucide-react';
import api from '../../services/api';

const WallpaperCategories = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    icon: '📁',
    color: '#6C63FF',
    order: 0,
    isActive: true,
  });

  const iconOptions = ['📁', '🌿', '🎨', '🎮', '🚀', '🐾', '🚗', '💻', '🏔️', '🌊', '🌸', '⚡'];
  const colorOptions = [
    '#6C63FF', '#00D9FF', '#4CAF50', '#FFA502', '#FF6348', 
    '#8B5CF6', '#EC4899', '#10B981', '#F59E0B', '#3B82F6'
  ];

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const { data } = await api.get('/wallpaper-categories');
      setCategories(data.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCategory) {
        await api.put(`/wallpaper-categories/${editingCategory.id}`, formData);
      } else {
        await api.post('/wallpaper-categories', formData);
      }
      fetchCategories();
      setShowDialog(false);
      resetForm();
    } catch (error) {
      console.error('Error saving category:', error);
    }
  };

  const toggleActive = async (id) => {
    try {
      await api.post(`/wallpaper-categories/${id}/toggle`);
      fetchCategories();
    } catch (error) {
      console.error('Error toggling active:', error);
    }
  };

  const deleteCategory = async (id) => {
    if (!window.confirm('Delete this category? All wallpapers in this category will be unassigned.')) return;
    try {
      await api.delete(`/wallpaper-categories/${id}`);
      fetchCategories();
    } catch (error) {
      console.error('Error deleting category:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      icon: '📁',
      color: '#6C63FF',
      order: 0,
      isActive: true,
    });
    setEditingCategory(null);
  };

  const openEditDialog = (category) => {
    setEditingCategory(category);
    setFormData({
      name: category.name,
      icon: category.icon || '📁',
      color: category.color,
      order: category.order,
      isActive: category.isActive,
    });
    setShowDialog(true);
  };

  if (loading) return <div className="text-center py-20">Loading...</div>;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Wallpaper Categories</h1>
          <p className="text-gray-400">Manage categories for wallpapers</p>
        </div>
        <button
          onClick={() => {
            resetForm();
            setShowDialog(true);
          }}
          className="bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 px-6 py-3 rounded-lg flex items-center gap-2"
        >
          <FolderPlus size={20} />
          Add Category
        </button>
      </div>

      {/* Categories Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {categories.map((category) => (
          <div
            key={category.id}
            className="gradient-card border border-gray-800 rounded-xl p-6"
            style={{
              borderColor: category.color + '40',
            }}
          >
            <div className="flex items-start gap-4">
              {/* Icon */}
              <div
                className="w-16 h-16 rounded-xl flex items-center justify-center text-3xl"
                style={{
                  backgroundColor: category.color + '20',
                }}
              >
                {category.icon || '📁'}
              </div>

              {/* Content */}
              <div className="flex-1">
                <h3 className="font-bold text-lg text-white mb-1">{category.name}</h3>
                <p className="text-sm text-gray-400 mb-3">Order: {category.order}</p>

                {/* Status Badge */}
                <span
                  className={`inline-block px-3 py-1 rounded-full text-xs font-bold ${
                    category.isActive
                      ? 'bg-green-500/20 text-green-400'
                      : 'bg-gray-700 text-gray-400'
                  }`}
                >
                  {category.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>

            {/* Actions */}
            <div className="mt-4 flex gap-2">
              <button
                onClick={() => toggleActive(category.id)}
                className={`flex-1 px-3 py-2 rounded-lg flex items-center justify-center gap-2 ${
                  category.isActive
                    ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30'
                    : 'bg-red-500/20 text-red-400 hover:bg-red-500/30'
                }`}
              >
                {category.isActive ? <Eye size={16} /> : <EyeOff size={16} />}
              </button>
              <button
                onClick={() => openEditDialog(category)}
                className="flex-1 px-3 py-2 rounded-lg bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 flex items-center justify-center"
              >
                <Edit2 size={16} />
              </button>
              <button
                onClick={() => deleteCategory(category.id)}
                className="flex-1 px-3 py-2 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30 flex items-center justify-center"
              >
                <Trash2 size={16} />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Add/Edit Dialog */}
      {showDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="gradient-card border border-gray-800 rounded-xl p-6 max-w-md w-full">
            <h2 className="text-2xl font-bold mb-6">
              {editingCategory ? 'Edit Category' : 'Add Category'}
            </h2>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium mb-2">Category Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-2 bg-dark border border-gray-700 rounded-lg focus:border-primary focus:outline-none"
                  placeholder="Nature, Abstract, Gaming..."
                  required
                />
              </div>

              {/* Icon Selection */}
              <div>
                <label className="block text-sm font-medium mb-2">Icon</label>
                <div className="grid grid-cols-6 gap-2">
                  {iconOptions.map((icon) => (
                    <button
                      key={icon}
                      type="button"
                      onClick={() => setFormData({ ...formData, icon })}
                      className={`p-3 text-2xl rounded-lg border-2 transition-all ${
                        formData.icon === icon
                          ? 'border-purple-500 bg-purple-500/20'
                          : 'border-gray-700 bg-dark hover:border-gray-600'
                      }`}
                    >
                      {icon}
                    </button>
                  ))}
                </div>
              </div>

              {/* Color Selection */}
              <div>
                <label className="block text-sm font-medium mb-2">Color</label>
                <div className="grid grid-cols-5 gap-2">
                  {colorOptions.map((color) => (
                    <button
                      key={color}
                      type="button"
                      onClick={() => setFormData({ ...formData, color })}
                      className={`w-full h-10 rounded-lg border-2 transition-all ${
                        formData.color === color
                          ? 'border-white scale-110'
                          : 'border-transparent'
                      }`}
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
              </div>

              {/* Order */}
              <div>
                <label className="block text-sm font-medium mb-2">Display Order</label>
                <input
                  type="number"
                  value={formData.order}
                  onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
                  className="w-full px-4 py-2 bg-dark border border-gray-700 rounded-lg focus:border-primary focus:outline-none"
                  min="0"
                />
              </div>

              {/* Active Checkbox */}
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  className="w-4 h-4"
                />
                <span>Active</span>
              </label>

              {/* Buttons */}
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowDialog(false);
                    resetForm();
                  }}
                  className="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 rounded-lg"
                >
                  {editingCategory ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default WallpaperCategories;